import { Page, Text, View, Document, StyleSheet } from "@react-pdf/renderer";
import moment from "moment";

function numberToWords(num) {
  const units = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
  ];
  const teens = [
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];
  const tens = [
    "",
    "ten",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ];

  if (typeof num === "string") num = parseFloat(num);
  if (isNaN(num)) return "Not a Number";

  const decimalSplit = num.toString().split(".");
  const integerPart = Math.abs(parseInt(decimalSplit[0], 10));
  const decimalPart = decimalSplit[1] ? decimalSplit[1] : "";

  if (integerPart === 0 && decimalPart === "") return "zero";

  let words = "";

  if (num < 0) words = "minus ";
  if (integerPart > 0) {
    words += convertIntegerPart(integerPart);
  } else {
    words += "zero";
  }
  if (decimalPart) {
    words += " point";
    for (let i = 0; i < decimalPart.length; i++) {
      const digit = parseInt(decimalPart[i], 10);
      words += " " + units[digit];
    }
  }

  return words.trim();
  function convertIntegerPart(num) {
    let words = "";

    if (Math.floor(num / 1000000) > 0) {
      words += convertIntegerPart(Math.floor(num / 1000000)) + " million ";
      num %= 1000000;
    }

    if (Math.floor(num / 1000) > 0) {
      words += convertIntegerPart(Math.floor(num / 1000)) + " thousand ";
      num %= 1000;
    }

    if (Math.floor(num / 100) > 0) {
      words += convertIntegerPart(Math.floor(num / 100)) + " hundred ";
      num %= 100;
    }
    if (num > 0) {
      if (words !== "") words += "and ";

      if (num < 10) {
        words += units[num];
      } else if (num < 20) {
        words += teens[num - 10];
      } else {
        words += tens[Math.floor(num / 10)];
        if (num % 10 > 0) {
          words += "-" + units[num % 10];
        }
      }
    }

    return words.trim();
  }
}

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 8,
    fontFamily: "Helvetica",
  },
  header: {
    textAlign: "center",
  },
  companyName: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 2,
  },
  companyAddress: {
    lineHeight: 0.8,
  },
  sectionContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    width: "100%",
  },
  column: {
    width: "32%", // slightly less than 33% to account for gaps
    flexShrink: 1,
    lineHeight: 0.8,
  },
  sectionTitle: {
    padding: 3,
    textAlign: "center",
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: "5px",
  },
  clientName: {
    fontWeight: "bold",
    marginBottom: 3,
  },
  clientName: {
    fontWeight: "bold",
  },
  table: {
    display: "flex",
    width: "100%",
    marginBottom: 15,
    borderWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 2,
  },
  tableHeader: {
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
  },
  tableCol: {
    width: "25%",
    padding: 2,
  },
  tableColSmall: {
    width: "12.5%",
  },
  invoiceInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  totalSection: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  terms: {
    marginTop: 20,
    lineHeight: 1.2,
    fontSize: 9,
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 30,
  },
  amountInWords: {
    marginTop: 10,
    fontStyle: "italic",
  },
  divider: {
    borderTopWidth: 1,
    borderTopColor: "#000",
    borderTopStyle: "solid",
    marginVertical: 5,
  },
});

const InvoicePDF = ({ data, sizes, loginuser }) => {
  const sub_data = data?.sub_cn_lines;
  // Fixed: Removed Math.min() and ensure minimum height
  const rowHeight = 20; // Approximate height per row in pixels
  const gst_number = loginuser?.u_profile?.cmp_gst?.slice(0, 2);
  const startsWith24 = data?.p_org_gst?.startsWith(gst_number);

  function roundOffAdjustment(amount) {
    const whole = Math.floor(amount);
    const decimal = amount - whole;

    if (decimal >= 0.5) {
      return whole + 1 - amount;
    } else {
      return -decimal;
    }
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={{ ...styles.companyName, marginBottom: "5px" }}>
            {loginuser?.u_profile?.cmp_name}
          </Text>
          <Text style={styles.companyAddress}>
            {loginuser?.u_profile?.cmp_addr_line1}
          </Text>
          <Text style={styles.companyAddress}>
            {loginuser?.u_profile?.cmp_addr_line2}
          </Text>
          <Text style={styles.companyAddress}>
            {loginuser?.u_profile?.cmp_city}-{loginuser?.u_profile?.cmp_pincode}
          </Text>
          <Text style={styles.companyAddress}>
            Mobile: {loginuser?.u_profile?.cmp_ph},
          </Text>
          <Text style={styles.companyAddress}>
            Email: {loginuser?.u_profile?.cmp_email}
          </Text>
          <Text style={{ ...styles.companyAddress, fontWeight: "bold" }}>
            GSTIN: {loginuser?.u_profile?.cmp_gst}
          </Text>
          <Text style={{ ...styles.companyAddress, fontWeight: "bold" }}>
            PAN: {loginuser?.u_profile?.cmp_pan}
          </Text>
        </View>
        <View style={styles.divider} />

        {/* cn To Section */}
        <View style={styles.sectionContainer}>
          {/* cn To Column */}
          <View style={styles.column}>
            <View
              style={{ ...styles.sectionTitle, backgroundColor: "#C0C0C0" }}
            >
              <Text>cn To</Text>
            </View>
            <Text style={styles.clientName}>{data?.p_org_name}</Text>
            <Text>{data?.p_org_address_line1}</Text>
            <Text>{data?.p_org_address_line2}</Text>
            <Text>{data?.p_org_pincode}</Text>
            <Text style={{ fontWeight: "bold" }}>
              GST # : {data?.p_org_gst}
            </Text>
          </View>

          {/* Ship To Column */}
          <View style={styles.column}>
            <View
              style={{ ...styles.sectionTitle, backgroundColor: "#C0C0C0" }}
            >
              <Text>Ship To</Text>
            </View>
            <Text style={styles.clientName}>{data?.p_org_name}</Text>
            <Text>{data?.p_org_address_line1}</Text>
            <Text>{data?.p_org_address_line2}</Text>
            <Text>{data?.p_org_pincode}</Text>
            <Text style={{ fontWeight: "bold" }}>
              GST # : {data?.p_org_gst}
            </Text>
          </View>

          {/* Invoice Info Column */}
          <View style={styles.column}>
            <View style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <View
                style={{
                  ...styles.sectionTitle,
                  backgroundColor: "#C0C0C0",
                }}
              >
                <Text>Tax Invoice</Text>
              </View>
              <Text style={{ fontWeight: "bold" }}>
                Invoice # : {data?.cn_invoice}
              </Text>
              <Text style={{ fontWeight: "bold" }}>
                Date :{" "}
                {moment(data?.cn_date, "YYYY-MM-DD HH:mm:ss").format(
                  "DD/MM/YYYY"
                )}
              </Text>
            </View>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={styles.tableCol}>Items</Text>
            <Text style={styles.tableCol}>HSN</Text>
            <Text style={styles.tableCol}>Size</Text>
            <Text style={styles.tableCol}>Units</Text>
            <Text style={styles.tableCol}>Weight</Text>
            <Text style={styles.tableCol}>Rate</Text>
            <Text style={styles.tableCol}>Amount</Text>
            <Text style={styles.tableCol}>Total</Text>
          </View>
          {/* Table Rows */}

          {/* Fixed: Ensure the container has enough height for single row */}
          <View style={{ minHeight: rowHeight }}>
            {sub_data?.map((row, index) => {
              const size = sizes?.data?.find(
                ({ size_id }) => size_id === row?.size_id
              );
              const size_data = size
                ? `${Number(size?.size_from)?.toFixed(2)} - ${Number(
                    size?.size_to
                  )?.toFixed(2)}`
                : "Fixed";

              return (
                <View style={{ ...styles.tableRow }} key={index}>
                  <Text
                    style={{ ...styles.tableCol, textTransform: "capitalize" }}
                  >
                    {"CERTIFICATE CHARGES"}
                  </Text>
                  <Text style={styles.tableCol}>-</Text>
                  <Text style={styles.tableCol}>-</Text>
                  <Text style={styles.tableCol}>{row.cn_pcs || "-"}</Text>
                  <Text style={styles.tableCol}>
                    {row.cn_cts?.toFixed(2) || "-"}
                  </Text>
                  <Text style={styles.tableCol}>{row?.cn_rate || "-"}</Text>
                  <Text style={styles.tableCol}>
                    {row?.cn_amount?.toFixed(2)}
                  </Text>
                  <Text style={styles.tableCol}>
                    {row?.cn_total?.toFixed(2)}
                  </Text>
                </View>
              );
            })}
          </View>
          <View>
            <View
              style={[
                styles.tableRow,
                styles.tableHeader,
                {
                  borderTopWidth: 1,
                  borderTopColor: "#000",
                  borderTopStyle: "solid",
                },
              ]}
            >
              <Text style={styles.tableColSmall}>GST %</Text>
              <Text style={styles.tableColSmall}>Taxable</Text>
              <Text style={styles.tableColSmall}>CGST</Text>
              <Text style={styles.tableColSmall}>SGST</Text>
              <Text style={styles.tableColSmall}>IGST</Text>
              <Text style={styles.tableColSmall}>Total</Text>
              <Text style={styles.tableColSmall}>Sub Total</Text>
              <Text style={styles.tableColSmall}>
                {Number(data?.cn_total)?.toFixed(2)}
              </Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableColSmall}>
                {" "}
                {Number(loginuser?.u_profile?.cmp_gst_rate)}
              </Text>
              <Text style={styles.tableColSmall}>
                {Number(data?.cn_total)?.toFixed(2)}
              </Text>
              <Text style={styles.tableColSmall}>
                {startsWith24 ? data?.cn_cgst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {startsWith24 ? data?.cn_sgst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {!startsWith24 ? data?.cn_igst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {data?.cn_net_amount?.toFixed(2)}
              </Text>
              <Text style={styles.tableColSmall}>GST</Text>
              <Text style={styles.tableColSmall}>
                {data?.cn_igst?.toFixed(2)}
              </Text>
            </View>
            <View
              style={{
                ...styles.tableRow,
                borderBottomWidth: 1,
                borderBottomColor: "#000",
                borderBottomStyle: "solid",
              }}
            >
              <Text style={styles.tableColSmall}>Total</Text>
              <Text style={styles.tableColSmall}>
                {Number(data?.cn_total)?.toFixed(2)}
              </Text>
              <Text style={styles.tableColSmall}>
                {startsWith24 ? data?.cn_cgst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {startsWith24 ? data?.cn_sgst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {!startsWith24 ? data?.cn_igst?.toFixed(2) : "00.00"}
              </Text>
              <Text style={styles.tableColSmall}>
                {data?.cn_net_amount?.toFixed(2)}
              </Text>
              <Text style={styles.tableColSmall}>Add/Less</Text>
              <Text style={styles.tableColSmall}>
                {roundOffAdjustment(data?.cn_net_amount).toFixed(2)}
              </Text>
            </View>
            <View style={styles.totalSection}>
              <Text style={styles.sectionTitle}>Net Amount</Text>
              <View
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <Text style={styles.sectionTitle}>
                  {(
                    data?.cn_net_amount +
                    roundOffAdjustment(data?.cn_net_amount)
                  ).toFixed(2)}
                </Text>
                <Text
                  style={{
                    fontWeight: "bold",
                    textTransform: "capitalize",
                    padding: 1,
                  }}
                >
                  {numberToWords(
                    (
                      data?.cn_net_amount +
                      roundOffAdjustment(data?.cn_net_amount)
                    ).toFixed(2)
                  ) + " only"}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.sectionContainer}>
          <View style={styles.column}>
            <Text
              style={{
                ...styles.sectionTitle,
                textAlign: "left",
                fontSize: 8,
                textDecoration: "underline",
              }}
            >
              Terms & Conditions
            </Text>
            <Text>Reverse charges applicable : No</Text>
          </View>
          <View style={styles.column}>
            <Text
              style={{
                ...styles.sectionTitle,
                textAlign: "left",
                fontSize: 8,
                textDecoration: "underline",
              }}
            >
              Bank Details
            </Text>
            <Text>NAME: {loginuser?.u_profile?.cmp_name}</Text>
            <Text>BANK: {loginuser?.u_profile?.bank_name}</Text>
            <Text>AC NO: {loginuser?.u_profile?.bank_acc_no}</Text>
            <Text>BRANCH: {loginuser?.u_profile?.bank_branch}</Text>
            <Text>IFSC: {loginuser?.u_profile?.bank_ifsc_code}</Text>
          </View>
          <View style={styles.column}>
            <Text
              style={{
                ...styles.sectionTitle,
                textAlign: "left",
                fontSize: 8,
              }}
            >
              For {loginuser?.u_profile?.cmp_name}
            </Text>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                marginTop: "60px",
              }}
            >
              <Text>Receiver's Signature</Text>
              <Text>Authorised Signature</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
